/**
 * Knowledge graph of the laboratory — the public map of what we think about.
 * Node ids are referenced from research.ts and by the AI action protocol.
 */

import type { GraphNode } from '../lab/types';

export const knowledgeNodes: GraphNode[] = [
  // ── Domains ────────────────────────────────────────────────────────────
  { id: 'ai', label: 'Artificial Intelligence', kind: 'domain', links: [] },
  { id: 'agents-domain', label: 'Agents', kind: 'domain', links: ['ai'] },
  { id: 'knowledge-domain', label: 'Knowledge', kind: 'domain', links: ['ai'] },
  { id: 'learning-domain', label: 'Human–Machine Learning', kind: 'domain', links: ['ai'] },
  { id: 'computation-domain', label: 'Computation', kind: 'domain', links: ['ai'] },
  { id: 'theory-domain', label: 'Theory', kind: 'domain', links: ['ai'] },

  // ── Agents branch ──────────────────────────────────────────────────────
  {
    id: 'multi-agent',
    label: 'Multi-Agent Systems',
    kind: 'topic',
    links: ['agents-domain'],
    research: ['agent-native-software', 'multi-agent-consensus', 'agent-orchestration'],
  },
  {
    id: 'agent-native',
    label: 'Agent-Native Software',
    kind: 'topic',
    links: ['agents-domain', 'multi-agent'],
    research: ['agent-native-software'],
    projects: ['agent-native-universe'],
  },
  {
    id: 'agent-economy',
    label: 'Resource Economies',
    kind: 'topic',
    links: ['multi-agent'],
    research: ['multi-agent-consensus'],
    projects: ['agent-native-universe'],
  },
  {
    id: 'consensus',
    label: 'Byzantine Agreement',
    kind: 'method',
    links: ['multi-agent', 'agent-economy'],
    research: ['multi-agent-consensus'],
  },
  {
    id: 'recursive-organization',
    label: 'Recursive Organization',
    kind: 'topic',
    links: ['agent-native', 'multi-agent'],
    research: ['agent-native-software'],
  },
  {
    id: 'orchestration',
    label: 'Agent Orchestration',
    kind: 'topic',
    links: ['multi-agent'],
    research: ['agent-orchestration'],
  },
  {
    id: 'autonomy-gating',
    label: 'Confidence-Gated Autonomy',
    kind: 'method',
    links: ['orchestration'],
    research: ['agent-orchestration'],
  },

  // ── Knowledge branch ───────────────────────────────────────────────────
  {
    id: 'knowledge-graph',
    label: 'Knowledge Graphs',
    kind: 'topic',
    links: ['knowledge-domain'],
    research: ['knowledge-graphs'],
    projects: ['knowledgebase-ai', 'studyninja'],
  },
  {
    id: 'semantic-retrieval',
    label: 'Semantic Retrieval',
    kind: 'method',
    links: ['knowledge-domain', 'knowledge-graph'],
    research: ['knowledge-graphs'],
    projects: ['knowledgebase-ai'],
  },
  {
    id: 'provenance',
    label: 'Knowledge Provenance',
    kind: 'method',
    links: ['knowledge-graph'],
    research: ['knowledge-graphs'],
  },
  {
    id: 'dynamic-weights',
    label: 'Dynamic Weights',
    kind: 'topic',
    links: ['knowledge-graph'],
    research: ['knowledge-graphs', 'adaptive-mastery'],
  },
  {
    id: 'adaptive-maps',
    label: 'Adaptive Maps',
    kind: 'topic',
    links: ['knowledge-graph', 'dynamic-weights'],
    research: ['adaptive-mastery'],
  },

  // ── Learning branch ────────────────────────────────────────────────────
  {
    id: 'mastery',
    label: 'Mastery Models',
    kind: 'topic',
    links: ['learning-domain'],
    research: ['adaptive-mastery'],
    projects: ['studyninja'],
  },
  {
    id: 'gap-diagnostics',
    label: 'Gap Diagnostics',
    kind: 'method',
    links: ['mastery', 'knowledge-graph'],
    research: ['adaptive-mastery'],
    projects: ['studyninja'],
  },
  {
    id: 'adaptive-paths',
    label: 'Adaptive Learning Paths',
    kind: 'method',
    links: ['mastery', 'adaptive-maps'],
    research: ['adaptive-mastery'],
    projects: ['studyninja'],
  },

  // ── Computation branch ─────────────────────────────────────────────────
  {
    id: 'numerical-stability',
    label: 'Numerical Stability',
    kind: 'topic',
    links: ['computation-domain'],
    research: ['compensated-numerics'],
    projects: ['balansis'],
  },
  {
    id: 'compensated-arithmetic',
    label: 'Compensated Arithmetic',
    kind: 'topic',
    links: ['numerical-stability'],
    research: ['compensated-numerics'],
    projects: ['balansis'],
  },
  {
    id: 'spiking-networks',
    label: 'Spiking Networks',
    kind: 'topic',
    links: ['computation-domain'],
    research: ['grown-networks'],
    projects: ['magicbrain'],
  },
  {
    id: 'local-learning',
    label: 'Local Learning Rules',
    kind: 'method',
    links: ['spiking-networks'],
    research: ['grown-networks'],
  },
  {
    id: 'structural-growth',
    label: 'Structural Growth',
    kind: 'topic',
    links: ['spiking-networks', 'local-learning'],
    research: ['grown-networks'],
  },

  // ── Theory branch ──────────────────────────────────────────────────────
  {
    id: 'falsifiability',
    label: 'Falsifiable Claims',
    kind: 'method',
    links: ['theory-domain'],
    research: ['unified-wholeness'],
  },
  {
    id: 'experiment-registry',
    label: 'Experiment Registry',
    kind: 'artifact',
    links: ['theory-domain', 'falsifiability'],
    research: ['unified-wholeness'],
    projects: ['uwt'],
  },
];

export const nodeById = new Map(knowledgeNodes.map((n) => [n.id, n]));
export const isNodeId = (s: string) => nodeById.has(s);

/** Undirected edge list, deduplicated, for renderers. */
export const knowledgeEdges: [string, string][] = (() => {
  const seen = new Set<string>();
  const out: [string, string][] = [];
  for (const n of knowledgeNodes) {
    for (const l of n.links) {
      if (!nodeById.has(l)) continue;
      const key = [n.id, l].sort().join('~');
      if (seen.has(key)) continue;
      seen.add(key);
      out.push([n.id, l]);
    }
  }
  return out;
})();
